/**
 * DroneViewer — Watch live broadcasts. Polls backend for WebM chunks.
 */
import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, WifiOff } from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

export default function DroneViewer() {
  const { roomId } = useParams();
  const videoRef = useRef(null);
  const msRef = useRef(null);
  const sbRef = useRef(null);
  const seenRef = useRef(new Set());
  const [status, setStatus] = useState("loading");

  const appendSegment = async (seq) => {
    const sb = sbRef.current;
    const ms = msRef.current;
    if (!sb || !ms || ms.readyState !== "open") return;
    try {
      const res = await fetch(API + "/broadcast/" + roomId + "/segment/" + seq);
      if (!res.ok) return false;
      const buf = await res.arrayBuffer();
      if (!buf || buf.byteLength < 100) return false;
      if (sb.updating) {
        await new Promise(r => { sb.onupdateend = () => { sb.onupdateend = null; r(); }; });
      }
      sb.appendBuffer(buf);
      return true;
    } catch { return false; }
  };

  useEffect(() => {
    if (!roomId) { setStatus("offline"); return; }
    const ms = new MediaSource();
    msRef.current = ms;
    if (videoRef.current) videoRef.current.src = URL.createObjectURL(ms);
    ms.onsourceopen = () => {
      try { sbRef.current = ms.addSourceBuffer('video/webm;codecs=vp8,opus'); } catch {
        try { sbRef.current = ms.addSourceBuffer('video/webm'); } catch { setStatus("offline"); return; }
      }
      sbRef.current.mode = 'segments';

      const poll = async () => {
        try {
          const res = await fetch(API + "/broadcast/" + roomId + "/chunks", { cache: "no-store" });
          if (!res.ok) return;
          const data = await res.json();
          if (!data.segments || data.count === 0) return;
          setStatus("live");
          for (const seq of data.segments) {
            if (seenRef.current.has(seq)) continue;
            const ok = await appendSegment(seq);
            if (ok) seenRef.current.add(seq);
          }
        } catch {}
      };

      setTimeout(poll, 500);
      const iv = setInterval(poll, 2500);
      return () => clearInterval(iv);
    };
    return () => {
      if (msRef.current && msRef.current.readyState !== "closed") msRef.current.endOfStream();
    };
  }, [roomId]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-white/60 hover:text-white text-xs">AllBaise</Link>
          {status === "live" && <span className="flex items-center gap-1 text-[10px] text-green-400"><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> LIVE</span>}
        </div>
      </div>
      <div className="w-full h-screen flex items-center justify-center bg-black">
        {status === "loading" && <div className="text-center"><Loader2 className="w-8 h-8 animate-spin text-white/50 mx-auto mb-3" /><p className="text-sm text-white/50">Waiting for broadcast...</p></div>}
        {status === "live" && <video ref={videoRef} autoPlay playsInline className="w-full h-full object-contain" />}
        {status === "offline" && <div className="text-center max-w-md px-6"><WifiOff className="w-12 h-12 text-white/30 mx-auto mb-4" /><h2 className="text-lg font-semibold text-white/70 mb-2">Offline</h2></div>}
      </div>
    </div>
  );
}
